import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2 } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";

const empty = {
  merk: "", model: "", bouwjaar: new Date().getFullYear(), prijs: 0, kilometerstand: 0,
  brandstof: "Benzine", transmissie: "Handgeschakeld", vermogen: "", kleur: "", carrosserie: "",
  beschrijving: "", images: [], verkocht: false, uitgelicht: false,
};

const brandstoffen = ["Benzine", "Diesel", "Elektrisch", "Hybride", "LPG"];
const transmissies = ["Handgeschakeld", "Automaat"];
const carrosserieen = ["Hatchback", "Sedan", "Stationwagen", "SUV", "Cabrio", "Coupé", "MPV"];

export const CarForm = ({ initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      }
      toast.success("Foto's geüpload");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImg = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.merk || !form.model) { toast.error("Vul merk en model in."); return; }
    setSaving(true);
    const payload = {
      ...form,
      bouwjaar: Number(form.bouwjaar), prijs: Number(form.prijs), kilometerstand: Number(form.kilometerstand),
    };
    try {
      if (initial?.id) await api.put(`/cars/${initial.id}`, payload);
      else await api.post("/cars", payload);
      toast.success(initial?.id ? "Auto bijgewerkt" : "Auto toegevoegd");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full bg-background border border-border px-3 py-2.5 outline-none focus:border-primary transition-colors text-sm";
  const lbl = "text-xs font-bold uppercase tracking-wide text-text-muted";

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="car-form-modal">
      <div className="bg-surface w-full max-w-2xl my-8 border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-surface z-10">
          <h3 className="font-display font-bold text-xl">{initial?.id ? "Auto bewerken" : "Nieuwe auto"}</h3>
          <button onClick={onCancel} data-testid="close-car-form" className="p-1 hover:text-primary"><X size={22} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field lbl="Merk"><input data-testid="form-merk" className={inp} value={form.merk} onChange={(e) => set("merk", e.target.value)} /></Field>
            <Field lbl="Model"><input data-testid="form-model" className={inp} value={form.model} onChange={(e) => set("model", e.target.value)} /></Field>
            <Field lbl="Bouwjaar"><input type="number" className={inp} value={form.bouwjaar} onChange={(e) => set("bouwjaar", e.target.value)} /></Field>
            <Field lbl="Prijs (€)"><input data-testid="form-prijs" type="number" className={inp} value={form.prijs} onChange={(e) => set("prijs", e.target.value)} /></Field>
            <Field lbl="Kilometerstand"><input type="number" className={inp} value={form.kilometerstand} onChange={(e) => set("kilometerstand", e.target.value)} /></Field>
            <Field lbl="Vermogen"><input className={inp} value={form.vermogen} onChange={(e) => set("vermogen", e.target.value)} placeholder="150 pk" /></Field>
            <Field lbl="Brandstof">
              <select className={inp} value={form.brandstof} onChange={(e) => set("brandstof", e.target.value)}>
                {brandstoffen.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field lbl="Transmissie">
              <select className={inp} value={form.transmissie} onChange={(e) => set("transmissie", e.target.value)}>
                {transmissies.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field lbl="Carrosserie">
              <select className={inp} value={form.carrosserie} onChange={(e) => set("carrosserie", e.target.value)}>
                <option value="">—</option>
                {carrosserieen.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field lbl="Kleur"><input className={inp} value={form.kleur} onChange={(e) => set("kleur", e.target.value)} /></Field>
          </div>

          <Field lbl="Beschrijving">
            <textarea rows={4} className={inp + " resize-none"} value={form.beschrijving} onChange={(e) => set("beschrijving", e.target.value)} />
          </Field>

          {/* Images */}
          <div>
            <p className={lbl}>Foto's</p>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-24 h-20 border border-border overflow-hidden group">
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                </div>
              ))}
              <button onClick={() => fileRef.current?.click()} data-testid="upload-btn" disabled={uploading}
                className="w-24 h-20 border-2 border-dashed border-border grid place-items-center text-text-muted hover:border-primary hover:text-primary transition-colors">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} />
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" checked={form.uitgelicht} onChange={(e) => set("uitgelicht", e.target.checked)} className="w-4 h-4 accent-[#004D40]" /> Uitgelicht
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" checked={form.verkocht} onChange={(e) => set("verkocht", e.target.checked)} className="w-4 h-4 accent-[#004D40]" /> Verkocht
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border sticky bottom-0 bg-surface">
          <button onClick={onCancel} className="rounded-full border border-border font-semibold px-6 py-2.5 hover:border-primary transition-colors">Annuleren</button>
          <button onClick={save} disabled={saving} data-testid="save-car-btn" className="rounded-full bg-primary text-white font-semibold px-6 py-2.5 hover:bg-primary-light transition-colors disabled:opacity-60">
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ lbl, children }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-text-muted mb-1.5">{lbl}</p>
    {children}
  </div>
);
